const HTTP_STATUS_MESSAGES = {
  400: 'Requisição inválida. Verifique os dados enviados.',
  401: 'Sessão expirada. Faça login novamente.',
  403: 'Acesso restrito.',
  404: 'Registro não encontrado.',
  409: 'Conflito: o registro foi alterado por outra operação.',
  429: 'Muitas requisições. Aguarde e tente novamente.',
  500: 'Erro interno no servidor.',
  502: 'Servidor indisponível. Tente novamente em instantes.',
  503: 'Serviço temporariamente indisponível.',
  504: 'Tempo de resposta esgotado. Tente novamente.',
};

const CONNECTION_FAILURE_MESSAGE = 'Falha de comunicação com o servidor.';
const GENERIC_FAILURE_MESSAGE = 'Não foi possível concluir a operação.';

const NON_DESCRIPTIVE_BODY_MESSAGES = new Set([
  'bad request',
  'unauthorized',
  'payment required',
  'forbidden',
  'not found',
  'method not allowed',
  'not acceptable',
  'request timeout',
  'conflict',
  'gone',
  'payload too large',
  'unsupported media type',
  'unprocessable entity',
  'too many requests',
  'internal server error',
  'not implemented',
  'bad gateway',
  'service unavailable',
  'gateway timeout',
  'server error on request',
]);

const SYNTHETIC_ERROR_MESSAGES = [
  /^network error$/i,
  /^request failed with status code \d{3}$/i,
  /^http \d{3}$/i,
  /^timeout of \d+ms exceeded$/i,
  /^timeout exceeded$/i,
  /^canceled$/i,
  /^err_[a-z_]+$/i,
];

const STATUS_IN_MESSAGE_PATTERNS = [/\bstatus code (\d{3})\b/i, /^http (\d{3})\b/i];

const nonEmptyString = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const toStatusCode = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^\d{3}$/.test(value.trim())) return Number(value.trim());
  return null;
};

const isBinaryBody = (body) => {
  if (typeof Blob !== 'undefined' && body instanceof Blob) return true;
  if (typeof ArrayBuffer === 'undefined') return false;
  return body instanceof ArrayBuffer || ArrayBuffer.isView(body);
};

const isPlainBody = (body) =>
  !!body && typeof body === 'object' && !Array.isArray(body) && !isBinaryBody(body);

const looksLikeHtmlPage = (text) => text.startsWith('<');

const isDescriptive = (text) => !!text && !NON_DESCRIPTIVE_BODY_MESSAGES.has(text.toLowerCase());

const descriptiveString = (value) => {
  const text = nonEmptyString(value);
  return isDescriptive(text) ? text : null;
};

const joinValidationMessages = (value) => {
  if (!Array.isArray(value)) return null;
  const parts = value.map(nonEmptyString).filter(Boolean);
  return parts.length === 0 ? null : parts.join('; ');
};

const messageFromBody = (body) => {
  if (body == null || isBinaryBody(body)) return null;

  if (typeof body === 'string') {
    const text = nonEmptyString(body);
    if (!text || looksLikeHtmlPage(text)) return null;
    return isDescriptive(text) ? text : null;
  }

  if (!isPlainBody(body)) return null;

  const nestMessage = joinValidationMessages(body.message) || descriptiveString(body.message);
  if (body.statusCode !== undefined && nestMessage) return nestMessage;

  return descriptiveString(body.error) || nestMessage;
};

const codeFromBody = (body) => (isPlainBody(body) ? nonEmptyString(body.code) : null);

const isErrorLike = (error) => !!error && typeof error === 'object';

const hasResponse = (error) => isErrorLike(error) && !!error.response && typeof error.response === 'object';

const responseBodyOf = (error) => (hasResponse(error) ? error.response.data : null);

const thrownBodyOf = (error) => (isErrorLike(error) ? error.body : null);

const statusFromMessage = (error) => {
  const text = isErrorLike(error) ? nonEmptyString(error.message) : null;
  if (!text) return null;
  for (const pattern of STATUS_IN_MESSAGE_PATTERNS) {
    const match = text.match(pattern);
    if (match) return toStatusCode(match[1]);
  }
  return null;
};

const isSyntheticMessage = (text) => SYNTHETIC_ERROR_MESSAGES.some((pattern) => pattern.test(text));

const authoredErrorMessage = (error) => {
  const text = isErrorLike(error) ? nonEmptyString(error.message) : null;
  if (!text || isSyntheticMessage(text)) return null;
  return isDescriptive(text) ? text : null;
};

export const extractStatus = (error) => {
  if (!isErrorLike(error)) return null;
  if (hasResponse(error)) {
    const responseStatus = toStatusCode(error.response.status);
    if (responseStatus !== null) return responseStatus;
  }
  const declaredStatus = toStatusCode(error.status);
  if (declaredStatus !== null) return declaredStatus;
  return statusFromMessage(error);
};

export const extractErrorCode = (error) => {
  if (!isErrorLike(error)) return null;
  return (
    codeFromBody(responseBodyOf(error)) ||
    codeFromBody(thrownBodyOf(error)) ||
    nonEmptyString(error.code)
  );
};

export const extractErrorMessage = (error, fallback) => {
  const fromResponseBody = messageFromBody(responseBodyOf(error));
  if (fromResponseBody) return fromResponseBody;

  const fromThrownBody = messageFromBody(thrownBodyOf(error));
  if (fromThrownBody) return fromThrownBody;

  const authored = authoredErrorMessage(error);
  if (authored) return authored;

  const status = extractStatus(error);
  const statusMessage = status === null ? null : HTTP_STATUS_MESSAGES[status];
  if (statusMessage) return statusMessage;

  if (status === null && !hasResponse(error)) return CONNECTION_FAILURE_MESSAGE;

  return nonEmptyString(fallback) || GENERIC_FAILURE_MESSAGE;
};

export const apiSuccess = (response, extra = {}) => ({
  ...extra,
  success: true,
  data: response ? response.data : null,
  error: null,
  code: null,
  status: response ? toStatusCode(response.status) : null,
});

export const apiFailure = (error, fallback, extra = {}) => ({
  success: false,
  data: null,
  error: extractErrorMessage(error, fallback),
  code: extractErrorCode(error),
  status: extractStatus(error),
  ...extra,
});

export const runRequest = async (send, options = {}) => {
  const { fallback, extra, select } = options;

  try {
    const response = await send();
    const envelope = apiSuccess(response, extra);
    if (typeof select !== 'function') return envelope;
    return { ...envelope, data: select(response) };
  } catch (error) {
    return apiFailure(error, fallback, extra);
  }
};

export const unwrap = (result, fallback) => (result && result.success ? result.data : fallback);

export const unwrapList = (result) =>
  result && result.success && Array.isArray(result.data) ? result.data : [];
